import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { MagnifyingGlass, Link as LinkIcon, X, CircleNotch } from "@phosphor-icons/react";

const fmt$ = (n) =>
  n == null || isNaN(n)
    ? "$0.00"
    : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Modal for picking a Zoho contact to link to a ZMEX customer.
 * Shared by Customer Profile (CustomerBillingPanel) and Billing Hub.
 *
 * Props:
 *   open          - boolean
 *   customer      - { id, business_name } | null
 *   onClose       - () => void
 *   onLinked      - (zoho_contact_id) => void   called after successful link
 *   initialSuggestion - optional pre-computed top suggestion to show first
 */
export default function ZohoLinkPickerModal({ open, customer, onClose, onLinked, initialSuggestion }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!open || !customer?.id) return;
    setSearchQ("");
    setSearchResults([]);
    setLoadingSuggestions(true);
    api.get(`/customers/${customer.id}/zoho-suggestions`)
      .then(({ data }) => setSuggestions(data.suggestions || []))
      .catch(() => setSuggestions([]))
      .finally(() => setLoadingSuggestions(false));
  }, [open, customer?.id]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  async function doSearch(q) {
    setSearchQ(q);
    if (!q.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get("/integrations/zoho/search-contacts", { params: { q } });
      setSearchResults(data.results || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function link(zohoContactId) {
    if (linking) return;
    setLinking(true);
    try {
      await api.post(`/customers/${customer.id}/zoho-link`, { zoho_contact_id: zohoContactId });
      toast.success(`${customer.business_name} linked to Zoho`);
      onLinked?.(zohoContactId);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link failed");
    } finally {
      setLinking(false);
    }
  }

  if (!open || !customer) return null;

  // De-dupe initial suggestion against the suggestions list so we don't show it twice.
  const initialId = initialSuggestion?.contact_id;
  const dedupedSuggestions = initialId
    ? suggestions.filter((s) => s.contact_id !== initialId)
    : suggestions;

  return (
    <div
      className="fixed inset-0 z-[80] bg-[#012340]/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="zoho-link-picker-modal"
    >
      <div
        className="bg-white rounded-sm shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-[#012340]/10 flex items-center justify-between gap-3 flex-shrink-0">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">Link Zoho Customer</div>
            <div className="font-heading text-lg font-bold text-[#012340] truncate" data-testid="zoho-picker-customer-name">
              {customer.business_name}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 grid place-items-center rounded-sm hover:bg-[#FAFAFA] text-[#5C738A]"
            data-testid="zoho-picker-close"
          >
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Initial suggestion (passed from row context) */}
          {initialSuggestion && (
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-2">Top Suggested Match</div>
              <SuggestionRow s={initialSuggestion} highlight onLink={() => link(initialSuggestion.contact_id)} linking={linking} />
            </div>
          )}

          {/* Other suggestions */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-2">
              {initialSuggestion ? "Other Suggestions" : "Smart-Match Suggestions"}
            </div>
            {loadingSuggestions && <div className="text-xs text-[#5C738A] flex items-center gap-1.5"><CircleNotch className="animate-spin" /> Loading…</div>}
            {!loadingSuggestions && dedupedSuggestions.length === 0 && (
              <div className="text-xs text-[#5C738A] py-2">No additional auto-matches. Use search below.</div>
            )}
            {!loadingSuggestions && (
              <div className="space-y-1.5">
                {dedupedSuggestions.map((s) => (
                  <SuggestionRow key={s.contact_id} s={s} onLink={() => link(s.contact_id)} linking={linking} />
                ))}
              </div>
            )}
          </div>

          {/* Manual search */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#5C738A] mb-2">
              Manual Match · Search Zoho Contacts
            </div>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C738A]" />
              <input
                value={searchQ}
                onChange={(e) => doSearch(e.target.value)}
                placeholder="Search by name, email, phone, or Zoho contact ID…"
                className="w-full pl-8 pr-3 py-2 rounded-sm border border-[#012340]/15 text-sm"
                data-testid="zoho-picker-search"
                autoFocus
              />
            </div>
            {searching && <div className="text-xs text-[#5C738A] mt-2 flex items-center gap-1"><CircleNotch className="animate-spin" /> Searching…</div>}
            {!searching && searchQ && searchResults.length === 0 && (
              <div className="text-xs text-[#5C738A] mt-2">No Zoho contacts match.</div>
            )}
            {searchResults.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {searchResults.map((r) => (
                  <div key={r.contact_id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm border border-[#012340]/10 hover:bg-[#FAFAFA]" data-testid={`zoho-picker-result-${r.contact_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#012340] truncate">{r.name || r.company || "—"}</div>
                      <div className="text-xs text-[#5C738A] truncate">
                        {[r.email, r.phone, `ID:${r.contact_id}`].filter(Boolean).join(" · ")}
                      </div>
                      {r.outstanding > 0 && (
                        <div className="text-xs text-[#E53935] font-mono">{fmt$(r.outstanding)} outstanding</div>
                      )}
                    </div>
                    <button
                      onClick={() => link(r.contact_id)}
                      disabled={linking}
                      className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm bg-[#CFAB7A] text-[#012340] hover:brightness-105 disabled:opacity-50 whitespace-nowrap"
                      data-testid={`zoho-picker-link-${r.contact_id}`}
                    >
                      <LinkIcon size={10} weight="bold" className="inline mr-1" /> Link
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SuggestionRow({ s, highlight, onLink, linking }) {
  return (
    <div
      className={`flex items-center justify-between gap-2 px-3 py-2 rounded-sm border ${highlight ? "border-[#CFAB7A] bg-[#CFAB7A]/8" : "border-[#012340]/10 hover:bg-[#FAFAFA]"}`}
      data-testid={`zoho-picker-suggestion-${s.contact_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#012340] truncate">{s.name}</div>
        <div className="text-xs text-[#5C738A] truncate">
          {[s.email, s.phone, `ID:${s.contact_id}`].filter(Boolean).join(" · ")}
        </div>
      </div>
      <span className="text-[10px] font-bold uppercase tracking-[0.18em] bg-[#CFAB7A]/25 text-[#012340] px-2 py-0.5 rounded-sm">{Math.round(s.score * 100)}%</span>
      <button
        onClick={onLink}
        disabled={linking}
        className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 rounded-sm bg-[#012340] text-white hover:bg-[#012340]/90 disabled:opacity-50 whitespace-nowrap"
        data-testid={`zoho-picker-approve-${s.contact_id}`}
      >
        Approve
      </button>
    </div>
  );
}
